import { httpClient } from '@shared/api';
import { Markdown } from '@shared/ui/Markdown';
import type { InboxFile, InboxItem, InboxMessage } from '@entities/operator';

/** Общие части переписки: кабинет («Запросы») и страница по ссылке из MAX. */

export function time(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  const today = new Date().toDateString() === d.toDateString();
  return d.toLocaleString(
    'ru-RU',
    today
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
  );
}

export function statusText(i: InboxItem): string {
  if (i.status === 'waiting') return 'ждёт оператора';
  if (i.status === 'active') return i.operator ? `ведёт ${i.operator}` : 'в работе';
  return 'завершён';
}

/** Лента переписки: клиент слева, ответы ИИ текстом, оператор справа, служебные по центру. */
export function Feed({ messages, item }: { messages: InboxMessage[]; item: InboxItem }) {
  return (
    <>
      {messages.map((m) => {
        if (m.sender === 'system') {
          return (
            <div key={m.id} className="op-sys">
              {m.text}
            </div>
          );
        }
        const who =
          m.sender === 'user'
            ? item.client
            : m.sender === 'ai'
              ? 'Ассистент'
              : (item.operator ?? 'Оператор');
        return (
          <div key={m.id} className={`op-msg is-${m.sender}`}>
            <span className="op-msg-who">
              {who} · {time(m.created_at)}
            </span>
            {m.sender === 'ai' ? (
              <div className="tc-md op-ai">
                <Markdown text={m.text} />
              </div>
            ) : (
              <>
                {m.files.length > 0 && <Files files={m.files} />}
                {bubbleText(m) && <span className="op-bubble">{linkify(bubbleText(m))}</span>}
              </>
            )}
          </div>
        );
      })}
    </>
  );
}

/** Текст пузыря без строк «📎 имя» — сами файлы показаны отдельно. */
function bubbleText(m: InboxMessage): string {
  if (!m.files.length) return m.text;
  return m.text
    .split('\n')
    .filter((l) => !l.startsWith('📎'))
    .join('\n')
    .trim();
}

function fileUrl(f: InboxFile): string {
  return `${String(httpClient.defaults.baseURL ?? '').replace(/\/$/, '')}${f.url}`;
}

function size(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} КБ`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
}

/** Файлы клиента: фото — превью (клик — крупно), документы — с настоящим именем. */
function Files({ files }: { files: InboxFile[] }) {
  return (
    <div className="op-files">
      {files.map((f) =>
        f.deleted ? (
          <span
            key={f.url}
            className="op-file is-gone"
            title="Файлы клиентов храним 30 дней после разговора"
          >
            <span className="op-file-body">
              <span className="op-file-name">{f.name}</span>
              <span className="op-file-size">удалён — храним 30 дней</span>
            </span>
          </span>
        ) : f.kind === 'image' ? (
          <a
            key={f.url}
            href={fileUrl(f)}
            target="_blank"
            rel="noopener noreferrer"
            className="op-photo"
          >
            <img src={fileUrl(f)} alt={f.name} loading="lazy" />
          </a>
        ) : (
          <a key={f.url} href={fileUrl(f)} download={f.name} className="op-file">
            <span className="op-file-ico" aria-hidden="true">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                <path d="M14 3v5h5" />
              </svg>
            </span>
            <span className="op-file-body">
              <span className="op-file-name">{f.name}</span>
              <span className="op-file-size">{size(f.size)} · скачать</span>
            </span>
          </a>
        ),
      )}
    </div>
  );
}

/** Ссылки на файлы клиента («📎 фото: https://…») делаем кликабельными. */
function linkify(text: string) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="v2-link">
        открыть ↗
      </a>
    ) : (
      p
    ),
  );
}
